/**
 * THE RELATIONSHIP WORKSPACE'S STATE, as rules rather than as component code
 * (#2351).
 *
 * Every behaviour #2351 resolved is here, in a pure reducer, for the reason
 * `relations.ts` gives about its own derivations: these are statements about
 * what the person is allowed to end up with, and a rule that lives inside a
 * component can only be checked by rendering one.
 *
 * The resolved calls, each implemented once:
 *
 *   · the basket CANNOT exceed the cardinality the database enforces, cannot
 *     hold a duplicate, and cannot hold an already-linked target;
 *   · switching relationship type with a dirty basket or form REQUIRES an
 *     explicit discard — it is never silently dropped and never silently kept;
 *   · `Save & add another` keeps the TYPE, clears the committed basket and the
 *     target form, and retains only edge facts deliberately marked reusable;
 *   · a refusal changes NOTHING: the basket, the query, the preview and every
 *     entered value survive, because the workspace is where the person recovers.
 *
 * It holds no ids it was not handed and no labels it invented: a basket entry
 * carries the canonical label the candidate list resolved, so a chip can never
 * become a uuid even if the record leaves the page.
 */
import type { CandidateSort } from './relationCandidates'

export type WorkspaceMode = 'link' | 'create'

/** Phone only: one region at a time, in the order #2351 names. */
export type WorkspaceStage = 'type' | 'target' | 'review'

export interface BasketEntry {
  id: string
  /** The canonical label, resolved when the record was chosen. Never an id. */
  label: string
}

export interface WorkspaceState {
  /** The selected relationship slot id, or null before anything is chosen. */
  slot: string | null
  mode: WorkspaceMode
  basket: BasketEntry[]
  /** The candidate whose preview is showing — follows focus in the list. */
  highlighted: string | null
  /** Edge facts, keyed by field name: valid_from and the declared attributes. */
  edge: Record<string, unknown>
  /** The create form's draft, including its namespaced edge facts. */
  draft: Record<string, unknown>
  /** A pending type switch waiting for an explicit discard decision. */
  confirming: { slot: string } | null
  /** What the last commit said, for the announcement region. */
  announcement: string | null
  stage: WorkspaceStage
  /** The candidate request is owned by `useCandidateRequest`; this is its sort. */
  sort: CandidateSort
}

export const EMPTY_WORKSPACE: WorkspaceState = {
  slot: null,
  mode: 'link',
  basket: [],
  highlighted: null,
  edge: {},
  draft: {},
  confirming: null,
  announcement: null,
  stage: 'type',
  sort: 'best',
}

/**
 * The edge facts a `Save & add another` keeps.
 *
 * #2351: *"It retains only edge defaults deliberately marked reusable."* Nothing
 * in the metamodel marks them, so the mark is made HERE and it is one field:
 * `valid_from`. A validity start is a property of the session's intent — the
 * person is stamping today's date across several links — while a job title, a
 * purpose or an environment is a property of the particular record just linked,
 * and carrying one of those onto the next link is how a batch acquires a fact
 * nobody entered for it.
 *
 * A declared attribute is therefore cleared, which is the conservative half:
 * the person retypes a purpose they wanted twice, rather than discovering that
 * three edges claim a purpose they set once.
 */
export const REUSABLE_EDGE_FACTS: readonly string[] = ['valid_from']

export function retainReusable(edge: Record<string, unknown>): Record<string, unknown> {
  const kept: Record<string, unknown> = {}
  for (const key of REUSABLE_EDGE_FACTS) {
    if (edge[key] != null && edge[key] !== '') kept[key] = edge[key]
  }
  return kept
}

/** Is there work in progress that a type switch would throw away? */
export function isDirty(state: WorkspaceState): boolean {
  if (state.basket.length > 0) return true
  if (Object.values(state.draft).some((v) => v != null && v !== '')) return true
  return Object.entries(state.edge).some(([key, v]) =>
    v != null && v !== '' && !REUSABLE_EDGE_FACTS.includes(key))
}

export type WorkspaceAction =
  | { kind: 'choose-type'; slot: string; force?: boolean }
  | { kind: 'discard-and-switch' }
  | { kind: 'keep-editing' }
  | { kind: 'mode'; mode: WorkspaceMode }
  | { kind: 'highlight'; id: string | null }
  | {
    kind: 'toggle'
    id: string
    label: string
    /** The cardinality cap from `slotMaxSelection` — 1, or null for unbounded. */
    max: number | null
    /** Refuses the add outright: an existing edge, or a row nothing may link. */
    blocked?: boolean
  }
  | { kind: 'remove'; id: string }
  | { kind: 'clear' }
  | { kind: 'edge'; patch: Record<string, unknown> }
  | { kind: 'draft'; patch: Record<string, unknown> }
  | { kind: 'sort'; sort: CandidateSort }
  | { kind: 'stage'; stage: WorkspaceStage }
  /** Phone: forward one step, or nowhere if this step is not satisfied yet. */
  | { kind: 'advance' }
  /** Phone: back one step, keeping every value the person has entered. */
  | { kind: 'retreat' }
  | { kind: 'announce'; message: string | null }
  /** A commit landed: keep the type, clear what was committed. */
  | { kind: 'committed'; message: string; keepOpen: boolean }
  /** A commit was refused: the state is preserved and the error is announced. */
  | { kind: 'refused'; message: string }

export function workspaceReducer(state: WorkspaceState, action: WorkspaceAction): WorkspaceState {
  switch (action.kind) {
    case 'choose-type': {
      if (action.slot === state.slot) return state
      /* AN EXPLICIT DECISION, never an inference. With work in progress the
         switch is held as `confirming` and nothing else moves — the basket, the
         draft and the edge facts are all still there behind the question, so
         "keep editing" is a real answer and not a re-entry. */
      if (!action.force && isDirty(state)) return { ...state, confirming: { slot: action.slot } }
      return {
        ...EMPTY_WORKSPACE,
        slot: action.slot,
        stage: state.stage === 'type' ? 'target' : state.stage,
        sort: state.sort,
      }
    }
    case 'discard-and-switch': {
      const slot = state.confirming?.slot
      if (!slot) return state
      return {
        ...EMPTY_WORKSPACE,
        slot,
        stage: state.stage === 'type' ? 'target' : state.stage,
        sort: state.sort,
      }
    }
    case 'keep-editing':
      return { ...state, confirming: null }
    case 'mode':
      /* The basket SURVIVES a mode change. Someone who has collected three
         people and then creates a fourth is doing one piece of work, and
         throwing the three away because they pressed "Create new" would be the
         lost-ticks defect the design doc names as Dynamics' worst. */
      return { ...state, mode: action.mode, highlighted: null }
    case 'highlight':
      return { ...state, highlighted: action.id }
    case 'toggle': {
      if (action.blocked) return state
      const present = state.basket.some((entry) => entry.id === action.id)
      if (present) return { ...state, basket: state.basket.filter((e) => e.id !== action.id) }
      const entry = { id: action.id, label: action.label }
      /* A SINGLE-CARDINALITY slot REPLACES rather than refuses. The database
         permits one overlapping target from this end, so a second tick is
         plainly "I meant this one instead" — and refusing it silently, with the
         first still ticked somewhere up the list, is the state a person cannot
         explain. The cap is never exceeded either way. */
      if (action.max === 1) return { ...state, basket: [entry] }
      if (action.max != null && state.basket.length >= action.max) return state
      return { ...state, basket: [...state.basket, entry] }
    }
    case 'remove':
      return { ...state, basket: state.basket.filter((e) => e.id !== action.id) }
    case 'clear':
      return { ...state, basket: [] }
    case 'edge':
      return { ...state, edge: { ...state.edge, ...action.patch } }
    case 'draft':
      return { ...state, draft: { ...state.draft, ...action.patch } }
    case 'sort':
      return { ...state, sort: action.sort }
    case 'stage':
      return { ...state, stage: normaliseStage({ ...state, stage: action.stage }) }
    case 'advance': {
      const next = nextStage(state)
      return next === state.stage ? state : { ...state, stage: next }
    }
    case 'retreat': {
      /* BACK PRESERVES EVERYTHING. The basket, the query, the draft and the
         edge facts are all untouched — #2358 S12.2 asks for exactly this, and
         it is the difference between a step and a restart. */
      const back: WorkspaceStage = state.stage === 'review' ? 'target' : 'type'
      return { ...state, stage: back }
    }
    case 'announce':
      return { ...state, announcement: action.message }
    case 'committed':
      /* KEEPS THE TYPE (#2351's resolved call 1), clears the committed basket
         and the target form, retains only the reusable edge facts — and lands
         back on the target stage on a phone, because "add another" means
         another target, not another type. */
      return {
        ...state,
        basket: [],
        draft: {},
        edge: retainReusable(state.edge),
        highlighted: null,
        confirming: null,
        announcement: action.message,
        stage: state.stage === 'review' ? 'target' : state.stage,
      }
    case 'refused':
      /* NOTHING MOVES. The basket, the draft, the edge facts, the highlight and
         the stage are all exactly as they were — the workspace is where the
         person recovers from a refusal, so it cannot be the thing the refusal
         resets. */
      return { ...state, announcement: action.message }
    default:
      return state
  }
}

/* ── the staged flow (phone) ───────────────────────────────────────────────
 *
 * Three steps, and the two normalisations ROOT ruled. They are functions of the
 * state rather than guards at the call site, because a step is reachable from
 * several directions — an advance, a Back, a discarded type switch, a commit
 * that emptied the basket — and a rule enforced at one of them is a rule that
 * holds sometimes.
 */
export const WORKSPACE_STAGES: readonly WorkspaceStage[] = ['type', 'target', 'review']

/** Is there anything on this step to carry forward? */
export function stageSatisfied(state: WorkspaceState, stage: WorkspaceStage): boolean {
  if (stage === 'type') return state.slot != null
  if (stage === 'target') {
    return state.mode === 'create'
      ? Object.values(state.draft).some((v) => v != null && v !== '')
      : state.basket.length > 0
  }
  return true
}

/**
 * The step this state is actually ON, after the two normalisations:
 * records without an eligible type is the type step, and review without a
 * basket or a draft is the records step.
 */
export function normaliseStage(state: WorkspaceState): WorkspaceStage {
  if (state.stage !== 'type' && !stageSatisfied(state, 'type')) return 'type'
  if (state.stage === 'review' && !stageSatisfied(state, 'target')) return 'target'
  return state.stage
}

/** One step forward, or the same step when this one is not satisfied. */
export function nextStage(state: WorkspaceState): WorkspaceStage {
  const at = normaliseStage(state)
  if (!stageSatisfied(state, at)) return at
  return at === 'type' ? 'target' : at === 'target' ? 'review' : 'review'
}

/**
 * The phone's ONE primary action: what it says, whether it can be used, and
 * whether using it steps forward or commits.
 *
 * The label is the truth about the next act rather than a generic Next —
 * `Review 2` says how many are coming with you, and `Link 2 records` says what
 * is about to be written. Both counts come from the basket, so neither can
 * claim a number the commit will not attempt.
 */
export interface StagedPrimary {
  label: string
  disabled: boolean
  act: 'advance' | 'commit'
}

export function stagedPrimary(
  state: WorkspaceState, offered: { link: boolean; create: boolean },
): StagedPrimary {
  const at = normaliseStage(state)
  if (at === 'type') {
    return { label: 'Pick a type', disabled: !stageSatisfied(state, 'type'), act: 'advance' }
  }
  if (at === 'target') {
    if (state.mode === 'create') {
      return {
        label: 'Review new record',
        disabled: !stageSatisfied(state, 'target'),
        act: 'advance',
      }
    }
    const n = state.basket.length
    return {
      label: n === 0 ? 'Select records' : `Review ${n}`,
      disabled: n === 0,
      act: 'advance',
    }
  }
  return { label: commitLabel(state), disabled: !commitReady(state, offered), act: 'commit' }
}

/* ── what the footer says, and what it does ───────────────────────────────── */

/**
 * The primary action's label — truthful about what is about to happen, and
 * never a count the basket does not hold.
 */
export function commitLabel(state: WorkspaceState): string {
  if (state.mode === 'create') return 'Create & link'
  const n = state.basket.length
  if (n === 0) return 'Link record'
  return n === 1 ? 'Link record' : `Link ${n} records`
}

/** Can the primary action run at all? */
export function commitReady(state: WorkspaceState, offered: { link: boolean; create: boolean }): boolean {
  if (!state.slot) return false
  if (state.mode === 'create') return offered.create
  return offered.link && state.basket.length > 0
}

/**
 * The outcome of a batch link, written from what actually happened.
 *
 * `core.mutate_relationship` takes ONE edge per call and this lane may not add
 * a write RPC, so N links are N calls and a partial failure is a real state.
 * The sentence says how many landed, the basket keeps exactly the ones that did
 * not, and nothing here calls the batch atomic.
 */
export interface BatchOutcome {
  linked: BasketEntry[]
  failed: { entry: BasketEntry; message: string }[]
}

export function batchSentence(outcome: BatchOutcome): string {
  const { linked, failed } = outcome
  const n = linked.length
  const one = (entries: BasketEntry[]) => entries.map((e) => e.label).join(', ')
  if (failed.length === 0) {
    return n === 1 ? `Linked ${one(linked)}.` : `Linked ${n} records.`
  }
  if (n === 0) {
    return failed.length === 1
      ? `${one(failed.map((f) => f.entry))} could not be linked: ${failed[0]!.message}`
      : `None of the ${failed.length} records could be linked. ${failed[0]!.message}`
  }
  return `Linked ${n} of ${n + failed.length}. ${one(failed.map((f) => f.entry))} could not be linked: ${failed[0]!.message}`
}
